/**
 * EmailPreview Component
 * Preview component showing rendered email with variable highlighting
 * Displays subject, body, and signature in email format
 */

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import {
  Clock,
  Paperclip,
  Star,
  Reply,
  Forward,
  MoreHorizontal,
  Braces,
} from 'lucide-react'

// ============================================
// Types
// ============================================

export interface EmailPreviewProps {
  subject?: string
  body: string
  signature?: string
  isHtml: boolean
  category?: 'Greeting' | 'Body' | 'Closing' | 'Signature' | 'Full'
  tone?: 'Formal' | 'Casual' | 'Friendly' | 'Professional'
  senderName?: string
  senderEmail?: string
  recipientName?: string
  recipientEmail?: string
  className?: string
}

// ============================================
// Sample Data for Preview
// ============================================

const SAMPLE_DATA: Record<string, string> = {
  userName: 'John Smith',
  userFirstName: 'John',
  userLastName: 'Smith',
  userEmail: 'john.smith@company.com',
  userTitle: 'Senior Manager',
  companyName: 'ACME Corporation',
  companyPhone: '+1 (555) 123-4567',
  companyEmail: 'info@acme.com',
  recipientName: 'Jane Doe',
  recipientFirstName: 'Jane',
  recipientEmail: 'jane.doe@client.com',
  recipientCompany: 'Client Corp',
  date: new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }),
  time: new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }),
}

// ============================================
// Helper Functions
// ============================================

/**
 * Replace variables with sample data and highlight them
 */
function processContent(content: string, highlight: boolean = true): string {
  return content.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
    const value = SAMPLE_DATA[varName] || match
    if (highlight) {
      return `<span class="variable-preview">${value}</span>`
    }
    return value
  })
}

/**
 * Extract variables from content
 */
function extractVariables(content: string): string[] {
  const regex = /\{\{(\w+)\}\}/g
  const matches: string[] = []
  let match: RegExpExecArray | null
  while ((match = regex.exec(content)) !== null) {
    const varName = match[1]
    if (varName && !matches.includes(varName)) {
      matches.push(varName)
    }
  }
  return matches
}

// ============================================
// Email Header Component
// ============================================

interface EmailHeaderProps {
  subject: string
  senderName: string
  senderEmail: string
  recipientName: string
  recipientEmail: string
}

function EmailHeader({
  subject,
  senderName,
  senderEmail,
  recipientName,
  recipientEmail,
}: EmailHeaderProps) {
  return (
    <div className="border-b border-neutral-200 dark:border-neutral-700 p-4">
      {/* Subject */}
      <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
        {subject || '(No Subject)'}
      </h2>

      {/* Sender Info */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-medium">
            {senderName.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-neutral-900 dark:text-white">
                {senderName}
              </span>
              <span className="text-sm text-neutral-500 dark:text-neutral-400">
                &lt;{senderEmail}&gt;
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
              <span>to</span>
              <span className="font-medium text-neutral-700 dark:text-neutral-300">
                {recipientName}
              </span>
              <span>&lt;{recipientEmail}&gt;</span>
            </div>
          </div>
        </div>

        {/* Timestamp & Actions */}
        <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
          <Clock className="h-4 w-4" />
          <span>Just now</span>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-800">
        <button className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500">
          <Reply className="h-4 w-4" />
        </button>
        <button className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500">
          <Forward className="h-4 w-4" />
        </button>
        <button className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500">
          <Star className="h-4 w-4" />
        </button>
        <button className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500">
          <Paperclip className="h-4 w-4" />
        </button>
        <button className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 ml-auto">
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

// ============================================
// Email Body Component
// ============================================

interface EmailBodyProps {
  content: string
  isHtml: boolean
}

function EmailBody({ content, isHtml }: EmailBodyProps) {
  const processedContent = useMemo(
    () => processContent(content, true),
    [content]
  )

  return (
    <div className="p-4">
      {isHtml ? (
        <div
          className="prose prose-neutral dark:prose-invert max-w-none text-sm"
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />
      ) : (
        <pre className="whitespace-pre-wrap font-sans text-sm text-neutral-700 dark:text-neutral-300">
          <div dangerouslySetInnerHTML={{ __html: processedContent }} />
        </pre>
      )}
    </div>
  )
}

// ============================================
// Signature Component
// ============================================

interface EmailSignatureProps {
  signature: string
  isHtml: boolean
}

function EmailSignature({ signature, isHtml }: EmailSignatureProps) {
  const processedSignature = useMemo(
    () => processContent(signature, true),
    [signature]
  )

  return (
    <div className="px-4 pb-4">
      <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
        {isHtml ? (
          <div
            className="prose prose-neutral dark:prose-invert prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: processedSignature }}
          />
        ) : (
          <pre className="whitespace-pre-wrap font-sans text-sm text-neutral-600 dark:text-neutral-400">
            <div dangerouslySetInnerHTML={{ __html: processedSignature }} />
          </pre>
        )}
      </div>
    </div>
  )
}

// ============================================
// Variable Legend Component
// ============================================

interface VariableLegendProps {
  variables: string[]
}

function VariableLegend({ variables }: VariableLegendProps) {
  if (variables.length === 0) return null

  return (
    <div className="p-3 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900">
      <div className="flex items-center gap-2 mb-2">
        <Braces className="h-4 w-4 text-blue-500" />
        <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
          Variables in this email ({variables.length})
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {variables.map((varName) => (
          <Badge
            key={varName}
            variant="secondary"
            className="font-mono text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
          >
            {`{{${varName}}}`}
            {SAMPLE_DATA[varName] && (
              <span className="ml-1 text-blue-500 dark:text-blue-400">
                = {SAMPLE_DATA[varName]}
              </span>
            )}
          </Badge>
        ))}
      </div>
    </div>
  )
}

// ============================================
// EmailPreview Component
// ============================================

export function EmailPreview({
  subject = '',
  body,
  signature,
  isHtml,
  category = 'Full',
  tone = 'Professional',
  senderName = 'John Smith',
  senderEmail = 'john.smith@company.com',
  recipientName = 'Jane Doe',
  recipientEmail = 'jane.doe@client.com',
  className,
}: EmailPreviewProps) {
  // Collect all variables from subject, body, and signature
  const allVariables = useMemo(() => {
    const vars: string[] = []
    extractVariables(subject).forEach((v) => {
      if (!vars.includes(v)) vars.push(v)
    })
    extractVariables(body).forEach((v) => {
      if (!vars.includes(v)) vars.push(v)
    })
    if (signature) {
      extractVariables(signature).forEach((v) => {
        if (!vars.includes(v)) vars.push(v)
      })
    }
    return vars
  }, [subject, body, signature])

  // Process subject
  const processedSubject = useMemo(
    () => processContent(subject, false),
    [subject]
  )

  return (
    <div
      className={cn(
        'rounded-xl border border-neutral-200 dark:border-neutral-700',
        'bg-white dark:bg-neutral-950',
        'overflow-hidden',
        className
      )}
    >
      {/* Category and Tone badges */}
      <div className="flex items-center gap-2 px-4 pt-3">
        <Badge variant="outline" className="text-xs">
          {category}
        </Badge>
        {tone && (
          <Badge variant="outline" className="text-xs">
            {tone}
          </Badge>
        )}
        <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-auto">
          Preview with sample data
        </span>
      </div>

      {/* Email Frame */}
      <div className="m-3 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-white dark:bg-neutral-900">
        {/* Email Header */}
        {category === 'Full' && (
          <EmailHeader
            subject={processedSubject}
            senderName={senderName}
            senderEmail={senderEmail}
            recipientName={recipientName}
            recipientEmail={recipientEmail}
          />
        )}

        {/* Email Body */}
        <ScrollArea className="max-h-[300px]">
          <EmailBody content={body} isHtml={isHtml} />

          {/* Signature */}
          {signature && (category === 'Full' || category === 'Signature') && (
            <EmailSignature signature={signature} isHtml={isHtml} />
          )}
        </ScrollArea>
      </div>

      {/* Variable Legend */}
      <VariableLegend variables={allVariables} />

      {/* Styles */}
      <style>{`
        .variable-preview {
          background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
          color: #1d4ed8;
          padding: 1px 4px;
          border-radius: 4px;
          font-weight: 500;
        }

        .dark .variable-preview {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(99, 102, 241, 0.2) 100%);
          color: #93c5fd;
        }
      `}</style>
    </div>
  )
}

export default EmailPreview
